// Pre-redesign Create Task modal, preserved verbatim behind the
// create_task_chart_clarity feature flag. Mirrors the current staging modal
// (incl. the custom DatePicker blur-commit fix); rendered for orgs without
// the flag. Delete this file once the redesign rolls out to everyone.
/* eslint-disable react/no-array-index-key */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import { parseFullName } from 'parse-full-name';
import axios from 'axios';

import {
  message,
  Input,
  Form,
  Checkbox,
  Select,
  AutoComplete,
  Tooltip,
  Alert,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import DatePicker from '../DatePicker';
import ModalWrapper from './ModalWrapper';
import { taskCreatedByStamp, buildTaskTemplateNotes } from '../../utils/taskTemplateNotes';
import { computeEmaStandaloneTaskFileName } from '../chartActions/helpers';
import { extractReplyTiffWarnings, showReplyTiffWarningModal } from '../chartActions/replyTiffWarning';
import { captureAnalytics } from '../../actions/preload';

const AUTOMATIC_ASSIGNEE_MARKER_TYPES = new Map([
  ['Ordering Provider', 'ordering_provider'],
  ['Ordering Provider Group', 'ordering_provider_group'],
]);

class CreateTaskLegacy extends Component {
  static manualAssignees(assignTo) {
    return (assignTo || []).filter((assignee) => !AUTOMATIC_ASSIGNEE_MARKER_TYPES.has(assignee));
  }

  constructor(props) {
    super(props);
    this.state = {
      sendToken: props.modalType.data.record,
      patientFirstName: this.props.modalType.data.patient_first_name || '',
      patientMiddleName: this.props.modalType.data.patient_middle_name || '',
      patientLastName: this.props.modalType.data.patient_last_name || '',
      patientDOB: this.props.modalType.data.dob || '',
      patientMrn: '',
      assignTo: undefined,
      autoUploadEmr: '',
      searchedNames: null,
      allProviderNames: [],
      task: '',
      searchedTasks: '',
      searchedTemplates: null,
      emaTaskTemplates: null,
      taskDetails: '',
      taskDetailsSearchOptions: null,
      dueDate: '',
      notes: taskCreatedByStamp(),
      priority: 'Normal',
      associateWithPatient: true,
      associateDoc: true,
      receiverType: this.props.modalType.data.receiverType,
      facilityName: '',
      facilityNames: [],
      searchedFacilities: [],
      apiCredentials: this.props.modalType.data.apiCredentials,
      showNameMismatchWarning: false,
      initialParsedName: '',
    };

    // Calculate initial name mismatch - this is determined once and never changes
    const normalizeSpaces = (str) => str ? str.trim().replace(/\s+/g, ' ') : '';
    const initialConstructedName = [
      this.props.modalType.data.patient_first_name,
      this.props.modalType.data.patient_middle_name,
      this.props.modalType.data.patient_last_name,
    ].filter(Boolean).join(' ').trim();
    const normalizedConstructedName = normalizeSpaces(initialConstructedName);
    const normalizedOriginalName = normalizeSpaces(this.props.modalType.data.name);

    this.state.initialParsedName = initialConstructedName;
    this.state.showNameMismatchWarning = this.props.modalType.data.name
                                         && normalizedConstructedName
                                         && normalizedOriginalName
                                         && normalizedConstructedName !== normalizedOriginalName;

    this.priorityOptions = ['Normal', 'High', 'STAT'];
    this.taskOptions = [
      'Authorization', 'Communication', 'Fax', 'Notify', 'Obtain', 'Order', 'Pathology', 'Prescribe', 'Reassure',
      'Recall', 'Re-Evaluate', 'Refer', 'Return To Clinic', 'Review', 'Schedule', 'Sign', 'Test',
    ];

    this.taskDetailsOptions = [
      'Additional Testing', 'Benign', 'Claim Review', 'Discuss at Follow-up', 'Follow-up Appt', 'Labs', 'PCP',
      'Medication', 'Normal', 'No Further Treatment', 'Procedure', 'Referring Provider', 'Results', 'Sign/Cosign',
      'Surgery',
    ];

    this.state.searchedTasks = this.taskOptions;
    this.state.taskDetailsSearchOptions = this.taskDetailsOptions;
    this.formRef = React.createRef();
  }

  async componentDidMount() {
    const { getEmaTaskProviders, getKareoTaskProviders, getTaskTemplatesByQuery } = this.props;
    const { sendToken } = this.state;
    const autoUploadEmr = localStorage.getItem('m8_auto_upload_emr');

    // Call the appropriate provider getter based on EMR type
    if (autoUploadEmr === 'Kareo') {
      await getKareoTaskProviders(sendToken);
    } else {
      await getEmaTaskProviders(sendToken);
    }

    // Get provider names from the appropriate source
    const providers = autoUploadEmr === 'Kareo'
      ? this.props.allKareoTaskProviders
      : this.props.allEmaTaskProviders;

    const providerNames = (providers || []).map((p) => {
      return typeof p === 'string' ? p : p?.name;
    }).filter(Boolean);

    if (providerNames.length === 1) {
      let newAssignTo = providerNames[0];
      if (autoUploadEmr === 'EMA') {
        // Set assign to as array
        newAssignTo = [newAssignTo];
      }
      this.setState({ assignTo: newAssignTo });
    }
    this.setState({ searchedNames: providerNames, allProviderNames: providerNames });
    this.setState({ autoUploadEmr });
    const query = `?email=${localStorage.getItem('m8_uid')}`;
    await getTaskTemplatesByQuery(query);
    this.setState({
      emaTaskTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
      searchedTemplates: this.props.allEmaTaskTemplates.ema_task_templates,
    });

    const { patientFirstName, patientLastName } = this.state;
    if (autoUploadEmr === 'EMA' && this.props.modalType.data.name) {
      const patientName = this.props.modalType.data.name;
      if (!(patientFirstName || patientLastName)) {
        // Handle edge case where the patient_name has 2 surnames (this is common for Spanish names)
        if (patientName.includes(',') && patientName.split(',')[0].trim().includes(' ') && patientName.split(',')[0].trim().split(' ').length > 1) {
          const parsedNames = this.parseName(patientName);
          this.setState({
            patientFirstName: parsedNames.firstName,
            patientLastName: parsedNames.lastName,
          });
        } else {
          this.setState({
            patientFirstName: parseFullName(patientName).first,
            patientMiddleName: parseFullName(patientName).middle,
            patientLastName: parseFullName(patientName).last,
          });
        }
      }
    }

    if (this.state.autoUploadEmr === 'EMA' && this.state.apiCredentials) {
      try {
        const response = await axios.get('/api/v1/emr_facilities');
        const res = await response.data;

        this.setState({
          facilityNames: res.facilities,
          searchedFacilities: res.facilities,
        });
      } catch (error) {
        console.error('Error fetching facilities:', error);
      }
    }
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    const { hideModal, triggerTaskCreation } = this.props;
    const {
      sendToken,
      patientFirstName,
      patientLastName,
      patientMiddleName,
      patientDOB,
      patientMrn,
      assignTo,
      task,
      taskDetails,
      dueDate,
      priority,
      notes,
      receiverType,
      associateWithPatient,
      associateDoc,
      facilityName,
    } = this.state;

    const isKareo = this.state.autoUploadEmr === 'Kareo';
    const standaloneFileName = computeEmaStandaloneTaskFileName({
      emr: this.state.autoUploadEmr,
      associateDoc,
      patientFirstName,
      patientMrn,
      record: this.props.modalType.data,
    });

    let formattedDOB = '';
    let formattedDueDate = '';
    if (patientDOB !== '') {
      const splitDate = patientDOB.split('/');
      formattedDOB = (associateWithPatient || isKareo) ? `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}` : '';
    }
    if (dueDate !== '') {
      const splitDate = dueDate.split('/');
      formattedDueDate = `${splitDate[2]}-${splitDate[0].padStart(2, '0')}-${splitDate[1].padStart(2, '0')}`;
    }

    // For Kareo, always associate with patient and document
    // For other EMRs, respect the checkbox values
    // The result is CAPTURED, not discarded: the reply merge runs for every Reply
    // parent before the endpoint branches on upload_type, so task_creation carries
    // the ENG-3012 partial-filing warning too. Dropping it told the user the task
    // succeeded while a visible TIFF never reached the chart.
    let taskResult;
    if (associateWithPatient || isKareo) {
      taskResult = await triggerTaskCreation({
        sendToken, receiverType, ...(standaloneFileName ? { fileName: standaloneFileName } : {}), patientName: '', patientFirstName, patientLastName, patientMiddleName, patientDOB: formattedDOB, patientMrn, assignTo, task, taskDetails, priority, notes, associateDoc: isKareo ? true : associateDoc, dueDate: formattedDueDate, facilityName,
      });
    } else {
      taskResult = await triggerTaskCreation({
        sendToken, receiverType, patientName: '', patientFirstName: '', patientLastName: '', patientMiddleName: '', patientDOB: '', patientMrn: '', assignTo, task, taskDetails, priority, notes, associateDoc, dueDate: formattedDueDate, facilityName,
      });
    }
    const { error, errorMessage } = this.props;
    hideModal();
    if (!error) {
      const tiffWarnings = extractReplyTiffWarnings(taskResult);
      if (tiffWarnings.length > 0) {
        showReplyTiffWarningModal({ warnings: tiffWarnings, workflow: 'legacy_task_creation' });
      } else {
        message.success({
          content: 'Task will be created shortly. We will notify you of any issues via email.',
        });
      }
    } else {
      message.error({
        content: errorMessage,
      });
    }
  };

  parseName(name) {
    let firstName = '';
    let lastName = '';
    const parts = name.split(',');
    lastName = parts[0].trim();
    firstName = parts[1].trim();

    // Handle edge cases for Spanish names with multiple surnames
    if (lastName.split(' ').length > 1 && !lastName.includes('-')) {
      const lastNames = lastName.split(' ');
      lastName = `${lastNames[0]} ${lastNames[1]}`;
    }

    return { firstName, lastName };
  }

  render() {
    const { isLoading, hideModal, allEmaTaskProviders, allKareoTaskProviders } = this.props;
    const isKareo = this.state.autoUploadEmr === 'Kareo';
    const allTaskProviders = isKareo ? allKareoTaskProviders : allEmaTaskProviders;
    const modalTitle = isKareo ? 'Create Note in Your EMR' : 'Create Task in Your EMR';
    const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'MMDDYYYY'];
    const customContentStyle = {
      width: '`1000px`',
    };
    const {
      patientDOB, patientMrn, notes, associateWithPatient, patientFirstName, patientLastName, patientMiddleName, dueDate,
    } = this.state;

    const disableSubmitButton = () => {
      // Base requirements for all EMRs
      const missingAssignee = (this.state.assignTo && this.state.assignTo.length === 0) || !this.state.assignTo;

      // For Kareo, patient info is always required (always associated with patient)
      // For other EMRs, only required if associateWithPatient is checked
      const missingPatientInfo = isKareo
        ? ((patientLastName === '' || patientFirstName === '' || patientDOB === ''))
        : (associateWithPatient && ((patientLastName === '' || patientFirstName === '' || patientDOB === '') && patientMrn === ''));

      // For Kareo, only assignee and patient info required
      if (isKareo) {
        return missingAssignee || missingPatientInfo;
      }

      // For other EMRs, task and taskDetails are also required
      return (
        missingPatientInfo
        || missingAssignee
        || this.state.task === ''
        || this.state.taskDetails === ''
      );
    };

    const handleAssociateWithPatient = (e) => {
      this.setState({ associateWithPatient: !e.target.checked });
    };

    const handleMiddleNameChange = (e) => {
      this.setState({ patientMiddleName: e.target.value });
    };

    const handleLastNameChange = (e) => {
      this.setState({ patientLastName: e.target.value });
    };

    const handleFirstNameChange = (e) => {
      this.setState({ patientFirstName: e.target.value });
    };

    const handleMrnChange = (e) => {
      this.setState({ patientMrn: e.target.value });
    };

    const handleDateChange = (_, dateString) => {
      this.setState({ patientDOB: dateString });
    };

    const handleDueDateChange = (_, dateString) => {
      this.setState({ dueDate: dateString });
    };

    const handleAssignToChange = (e) => {
      this.setState({ assignTo: e });
    };

    const handleAssignToSearch = (e) => {
      const allNames = this.state.allProviderNames || [];

      if (e) {
        const filtered = allNames.filter((n) => n.toLowerCase().includes(e.toLowerCase()));
        this.setState({ searchedNames: filtered });
      } else {
        this.setState({ searchedNames: allNames });
      }
    };

    const handleFacilitySearch = (value) => {
      if (value) {
        const filtered = this.state.facilityNames.filter(facility =>
          facility.name.toLowerCase().includes(value.toLowerCase())
        );
        this.setState({ searchedFacilities: filtered });
      } else {
        this.setState({ searchedFacilities: this.state.facilityNames });
      }
    };

    const handleFacilityChange = (value) => {
      if (!value) {
        this.setState({ facilityName: '', facilityId: null });
        return;
      }
      const selectedFacility = this.state.searchedFacilities.find(facility => facility.name === value);
      this.setState({ facilityName: selectedFacility.name, facilityId: selectedFacility.id });
    };

    const handleTaskSearch = (e) => {
      if (e) {
        const filtered = this.taskOptions.filter(task => task.toLowerCase().includes(e.toLowerCase()));
        this.setState({ searchedTasks: filtered });
      } else {
        this.setState({ searchedTasks: this.taskOptions });
      }
    };

    const handleTaskChange = (e) => {
      this.setState({ task: e });
    };

    const handleSearchTaskDetails = (e) => {
      this.setState({ taskDetails: e });
      const filteredOptions = this.taskDetailsOptions.filter(option => (
        option.toLowerCase().includes(e.toLowerCase())));
      this.setState({ taskDetailsSearchOptions: filteredOptions });
    };

    const handleTaskDetailsSelection = (e) => {
      this.setState({ taskDetails: e });
    };

    const handleNotesChange = (e) => {
      this.setState({ notes: e.target.value });
    };

    const handleAssociateDoct = (e) => {
      this.setState({ associateDoc: !e.target.checked });
    };

    const handlePriorityChange = (e) => {
      this.setState({ priority: e });
    };

    const handleTaskTemplateSearch = (e) => {
      const unfilteredObjects = this.state.emaTaskTemplates;
      if (e) {
        const filtered = this.state.emaTaskTemplates.map(template => template.task_name).filter(option => (option.toLowerCase().includes(e.toLowerCase())));
        const filteredObject = [];
        filtered.forEach((template) => {
          const index = this.state.emaTaskTemplates.map(t => t.task_name).indexOf(template);
          filteredObject.push(this.state.emaTaskTemplates[index]);
        });
        this.setState({ searchedTemplates: filteredObject });
      } else {
        this.setState({ searchedTemplates: unfilteredObjects });
      }
    };

    const handleTaskTemplateChange = (e) => {
      if (isKareo) {
        captureAnalytics('tebra_note_template_selection_changed', {
          action: e ? 'selected' : 'cleared',
          workflow: 'create_note',
          modal_variant: 'legacy',
        });
      }
      // if user clears out selection, we will reload the list of templates to choose from.
      if (!e) {
        handleTaskTemplateSearch();
        this.setState({
          notes: taskCreatedByStamp(),
        });
        return;
      }
      const index = this.state.emaTaskTemplates.map(template => template.id).indexOf(e);
      const selectedTemplate = this.state.emaTaskTemplates[index];
      // Ordering Provider markers are resolved only during automatic tasking.
      // A manual task has no document-processing step to resolve either marker,
      // so leave the assignee blank until the user chooses a real EMA assignee.
      const assigtoArray = CreateTaskLegacy.manualAssignees(selectedTemplate.assign_to_as_array);
      const clearedMarkerTypes = [];
      (selectedTemplate.assign_to_as_array || []).forEach((assignee) => {
        const markerType = AUTOMATIC_ASSIGNEE_MARKER_TYPES.get(assignee);
        if (markerType) clearedMarkerTypes.push(markerType);
      });
      handlePriorityChange(selectedTemplate.priority);
      handleTaskDetailsSelection(selectedTemplate.task_details);
      handleTaskChange(selectedTemplate.task_type);
      handleAssignToChange(isKareo ? assigtoArray[0] : assigtoArray);
      if (clearedMarkerTypes.length > 0) {
        captureAnalytics('manual_task_template_assignee_marker_cleared', {
          assignee_marker_types: clearedMarkerTypes,
          modal_variant: 'legacy',
        });
      }

      // Force both the state update and re-render
      const newNotes = buildTaskTemplateNotes(selectedTemplate.notes);
      this.setState({ notes: newNotes }, () => {
        // Get form instance from ref
        if (this.formRef.current) {
          this.formRef.current.setFieldsValue({
            notes: newNotes,
          });
        }
      });
    };

    const noProviderMessage = 'Contact your Admin to add EMR Providers';

    return (
      <ModalWrapper
        hideModal={hideModal}
        customContentStyle={customContentStyle}
        action={this.handleSubmit}
        actionName="Submit"
        width="700px"
        dismiss={isLoading ? '' : 'Cancel'}
        disabled={disableSubmitButton()}
        modalTitle={modalTitle}
        form="createTask"
        isDraggable
      >
        <Form
          ref={this.formRef}
          id="createTask"
          onFinish={hideModal}
          layout="vertical"
          initialValues={{
            patientFirstName,
            patientMiddleName,
            patientLastName,
            notes,
            dueDate,
          }}
        >
          {this.state.showNameMismatchWarning && (
            <Alert
              message="Patient Name Mismatch Detected"
              description={(
                <div>
                  Please verify the patient's name before sending to chart. Due to a recent
                  system migration, some previously edited values may be inconsistent.
                  <br />
                  <br />
                  Current name values:
                  <br />
                  Extracted name: <span style={{ color: '#722ed1' }}>{this.state.initialParsedName}</span>
                  <br />
                  Updated name: <span style={{ color: '#722ed1' }}>{this.props.modalType.data.name}</span>
                </div>
              )}
            type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          )}
          {this.state.autoUploadEmr !== 'Kareo' && (
            <Form.Item
              name="remember"
              valuePropName="checked"
              onChange={handleAssociateWithPatient}
              checked={associateWithPatient}
            >
              <Checkbox>Don't associate task with patient chart</Checkbox>
            </Form.Item>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            {(associateWithPatient || this.state.autoUploadEmr === 'Kareo') && (
              <div style={{ display: 'flex', gap: '10px' }}>
                <Form.Item
                  label="Patient First Name"
                  onChange={handleFirstNameChange}
                  style={{ flex: 1 }}
                >
                  <Input
                    name="patientFirstName"
                    placeholder="First name"
                    value={patientFirstName}
                  />
                </Form.Item>
                <Form.Item
                  label="Patient Middle Name"
                  onChange={handleMiddleNameChange}
                  style={{ flex: 1 }}
                >
                  <Input
                    name="patientMiddleName"
                    placeholder="Middle name"
                    value={patientMiddleName}
                  />
                </Form.Item>
                <Form.Item
                  label="Patient Last Name"
                  onChange={handleLastNameChange}
                  style={{ flex: 1 }}
                >
                  <Input
                    name="patientLastName"
                    placeholder="Last name"
                    value={patientLastName}
                  />
                </Form.Item>
              </div>
            )}

            {(associateWithPatient || this.state.autoUploadEmr === 'Kareo') && (
            <Form.Item
              label="Patient Date of Birth"
            >
              <DatePicker
                onChange={handleDateChange}
                name="patientDOB"
                format={dateFormat}
                value={patientDOB && moment(patientDOB, dateFormat)}
              />
            </Form.Item>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {associateWithPatient && !isKareo
            && (
            <Form.Item
              onChange={handleMrnChange}
              name="patientMRN"
              label="Patient MRN (Optional)"
              style={{ flex: 1 }}
            >
              <Input placeholder="Patient MRN" />
            </Form.Item>
            )}

           {this.state.autoUploadEmr === 'EMA' && this.state.apiCredentials
          && (
          <Form.Item
              label="Facility (Optional)"
              style={{ flex: 0.4 }}
            >
              <Select
                showSearch
                placeholder="Facility"
                value={this.state.facilityName}
                optionFilterProp="children"
                onChange={handleFacilityChange}
                onSearch={handleFacilitySearch}
                filterOption={false}
                allowClear
              >
                {this.state.searchedFacilities.map(facility => (
                  <Select.Option key={facility.id} value={facility.name}>
                    {facility.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          )}
          </div>

          <div style={{ display: 'flex', gap: '10px' }}>
            {['EMA', 'Kareo'].includes(this.state.autoUploadEmr)
            && (
            <Form.Item
              label={isKareo ? 'Note Templates' : 'Task Templates'}
              style={{ flex: 0.5 }}
            >
              <Select
                showSearch
                optionFilterProp="children"
                onChange={handleTaskTemplateChange}
                onSearch={handleTaskTemplateSearch}
                filterOption={false}
                allowClear
              >
                {this.state.searchedTemplates && this.state.searchedTemplates.map(e => (
                  <Select.Option key={e.id} value={e.id}>
                    {e.task_name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            )}
          </div>
          {!isKareo && (
            <div style={{ display: 'flex', gap: '10px' }}>
              <Form.Item
                label="Task"
                style={{ flex: 0.4 }}
              >
                <Select
                  showSearch
                  placeholder="Task"
                  value={this.state.task}
                  optionFilterProp="children"
                  onChange={handleTaskChange}
                  onSearch={handleTaskSearch}
                  filterOption={false}
                >
                  {this.state.searchedTasks.map(e => (
                    <Select.Option key={e} value={e}>
                      {e}
                    </Select.Option>
                  ))}
                </Select>

              </Form.Item>

              <Form.Item
                label="Task Details"
                style={{ flex: 0.4 }}
              >
                <AutoComplete
                  placeholder="Task Details"
                  options={this.state.taskDetailsSearchOptions.map(option => ({ value: option }))}
                  value={this.state.taskDetails}
                  onSearch={handleSearchTaskDetails}
                  onSelect={handleTaskDetailsSelection}
                />
              </Form.Item>

              <Form.Item
                label="Due Date"
                style={{ flex: 0.2 }}
              >
                <DatePicker
                  onChange={handleDueDateChange}
                  name="dueDate"
                  format={dateFormat}
                  value={dueDate && moment(dueDate, dateFormat)}
                />
              </Form.Item>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            <Form.Item
              label={allTaskProviders && allTaskProviders.length === 0
                ? (
                  <div>
                    Assign to EMR Staff or Staff Group
                    <Tooltip title="Please contact your Admin to add Providers">
                      <InfoCircleOutlined style={{ paddingLeft: '2px', marginTop: '6px', height: '8px' }} />
                    </Tooltip>
                  </div>
                ) : (
                  <div>
                    Assign to EMR Staff or Staff Group
                  </div>
                )}
              style={{ flex: 0.5 }}
            >
              <Select
                mode={isKareo ? undefined : "multiple"}
                showSearch
                placeholder="Select a provider"
                value={this.state.assignTo}
                optionFilterProp="children"
                onChange={handleAssignToChange}
                onSearch={handleAssignToSearch}
                filterOption={false}
                allowClear
                notFoundContent={noProviderMessage}
              >
                {this.state.searchedNames && this.state.searchedNames.map((e, index) => (
                  <Select.Option key={index} value={e}>
                    {e}
                  </Select.Option>
                ))}
              </Select>

            </Form.Item>

            {!isKareo && (
              <Form.Item
                label="Priority"
                style={{ flex: 0.5 }}
              >
                <Select
                  allowClear={false}
                  options={this.priorityOptions.map(option => ({ value: option }))}
                  onChange={handlePriorityChange}
                  value={this.state.priority}
                />
              </Form.Item>
            )}
          </div>

          <Form.Item
            name="notes"
            label="Notes (optional)"
            >
            <Input.TextArea
              onChange={handleNotesChange}
              placeholder="Notes (optional)"
              name="notes"
              value={this.state.notes}
            />
          </Form.Item>

          {this.state.autoUploadEmr !== 'Kareo' && (
            <Form.Item
              onChange={handleAssociateDoct}
              name="assocDocToTask"
              valuePropName="checked"
            >
              <Checkbox>Don't associate document with task</Checkbox>
            </Form.Item>
          )}
        </Form>
      </ModalWrapper>
    );
  }
}

CreateTaskLegacy.defaultProps = {
  error: false,
  errorMessage: null,
  allEmaTaskTemplates: {},
};

CreateTaskLegacy.propTypes = {
  hideModal: PropTypes.func.isRequired,
  modalType: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  triggerTaskCreation: PropTypes.func.isRequired,
  error: PropTypes.bool,
  allEmaTaskProviders: PropTypes.array.isRequired,
  allKareoTaskProviders: PropTypes.array.isRequired,
  getEmaTaskProviders: PropTypes.func.isRequired,
  getKareoTaskProviders: PropTypes.func.isRequired,
  getTaskTemplatesByQuery: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
  allEmaTaskTemplates: PropTypes.object,
};

export default connect(state => ({
  allEmaTaskTemplates: state.userProfile.allEmaTaskTemplates,
  isLoading: state.records.isLoading,
  emailValidMessage: state.records.emailValidMessage,
  updatedLabel: state.records.updatedLabel,
  data: state.labels.data,
  selectedRecord: state.inboxTable.recordData,
  error: state.records.error,
  emaTaskProviders: state.auth,
  allEmaTaskProviders: state.userProfile.allEmaTaskProviders,
  allKareoTaskProviders: state.userProfile.allKareoTaskProviders,
  errorMessage: state.records.errorMessage,
  apiCredentials: state.records.apiCredentials,
}))(CreateTaskLegacy);
